import { useCallback, useRef, useState } from "react";

/**
 * Copy-to-clipboard with a transient "copied" flag for confirmation UI.
 *
 * Shared by every click-to-copy affordance (CopyablePhone, phone chips, …) so
 * the clipboard fallback + reset-timer behaviour lives in exactly one place.
 * Returns `copied` (true for `resetMs` after a successful copy) and `copy(text)`
 * which resolves to whether the write succeeded — the Clipboard API throws in
 * insecure contexts / when permission is denied, and we swallow that so the
 * caller's row/card click handling is never broken by a failed copy.
 */
export function useCopyToClipboard(resetMs = 1200): { copied: boolean; copy: (text: string) => Promise<boolean> } {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), resetMs);
        return true;
      } catch {
        return false;
      }
    },
    [resetMs]
  );

  return { copied, copy };
}
